import { motion } from 'framer-motion';
import {
  UserPlus,
  MessageSquare,
  Users,
  BookOpen,
  DollarSign,
  Bus,
  ClipboardCheck,
  Award,
  BarChart3,
  MapPinned,
  Send,
  Bell,
  Globe,
  CalendarDays,
  NotebookPen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function FeaturesPage() {
  const features = [
    {
      icon: UserPlus,
      title: 'Student Enrollment & Profile Management',
      description:
        'Streamlined admission process with digital forms, document upload, and automated enrollment. Track student history from admission to alumni status.',
      color: 'bg-blue-500',
    },
    {
      icon: BookOpen,
      title: 'Course & Curriculum Management',
      description:
        'Design curriculum, manage subjects, and track course completion. Flexible course structures for different academic levels.',
      color: 'bg-yellow-500',
    },
    {
      icon: Users,
      title: 'Classroom & Section Management',
      description:
        'Create and manage classes, sections, and student assignments. Smart class allocation based on capacity and requirements.',
      color: 'bg-green-500',
    },
    {
      icon: DollarSign,
      title: 'Fee Collection & Financial Management',
      description:
        'Complete fee lifecycle management - invoicing, collection, reminders, and receipts. Support for multiple payment modes and installments.',
      color: 'bg-emerald-500',
    },
    {
      icon: MessageSquare,
      title: 'Lead & Enquiry Management System',
      description:
        'Capture and track all prospective student enquiries. Automated follow-ups and conversion tracking to improve admissions.',
      color: 'bg-purple-500',
    },
    {
      icon: ClipboardCheck,
      title: 'Examination & Assessment Management',
      description:
        'Create exam schedules, manage question banks, and conduct assessments. Automated grading and result publishing.',
      color: 'bg-red-500',
    },
    {
      icon: CalendarDays,
      title: 'Holiday Management',
      description:
        'Plan and publish school holidays, vacations, and special days in one place. Keep staff, students, and parents aligned with a clear academic calendar.',
      color: 'bg-rose-500',
    },
    {
      icon: NotebookPen,
      title: 'Homework Management',
      description:
        'Assign homework by class or section, set due dates, and track submissions. Teachers and parents stay updated on daily learning tasks.',
      color: 'bg-violet-500',
    },
    {
      icon: Award,
      title: 'Certificate & Document Management System',
      description:
        'Generate and manage certificates, transcripts, and academic documents. Digital certificates with verification system.',
      color: 'bg-indigo-500',
    },
    {
      icon: Bus,
      title: 'Fleet & Transport Management System',
      description:
        'Route planning, vehicle tracking, and driver management. Real-time GPS tracking for enhanced student safety.',
      color: 'bg-orange-500',
    },
    {
      icon: BarChart3,
      title: 'Reports, Insights & Analytics Dashboard',
      description:
        'Comprehensive reports and dashboards for data-driven decisions. Track attendance, performance, fees, and more in real-time.',
      color: 'bg-pink-500',
    },
    {
      icon: MapPinned,
      title: 'Real-Time Vehicle Tracking & Monitoring',
      description:
        'Live GPS tracking for school buses and fleet vehicles. Monitor routes, speed, and stop times in real time. Parents receive accurate pickup and drop alerts for safer commutes.',
      color: 'bg-cyan-600',
    },
    {
      icon: Send,
      title: 'WhatsApp Communication Gateway',
      description:
        'Send automated notifications, fee reminders, and announcements directly to parents via WhatsApp. Improve communication reach.',
      color: 'bg-green-600',
    },
    {
      icon: Bell,
      title: 'Notice & Announcement Center',
      description:
        'Broadcast important updates to students, parents, and staff instantly. Schedule announcements and track engagement.',
      color: 'bg-blue-600',
    },
    {
      icon: Globe,
      title: 'Website & Digital Presence Management',
      description:
        'Professional website for your school with CMS integration. Showcase achievements, events, and connect with community.',
      color: 'bg-teal-500',
    },
  ];

  return (
    <div data-testid="features-page" className="pt-20">
      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black mb-6 font-heading">
              Powerful Features For Modern Schools
            </h1>
            <p className="text-base leading-relaxed text-slate-300 max-w-3xl mx-auto">
              Comprehensive suite of tools designed to automate every aspect of
              school management. From admissions to alumni, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      <section
        data-testid="features-grid-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`feature-module-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 feature-card"
              >
                <div
                  className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="whatsapp-showcase-section"
        className="py-24 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-green-100 px-4 py-2 rounded-full mb-6">
                <span className="text-green-700 font-medium text-sm">
                  Most Popular Feature
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
                WhatsApp Powered Communication
              </h2>
              <p className="text-base text-slate-600 mb-6">
                Reach parents instantly on their preferred platform. Send fee
                reminders, exam schedules, attendance updates, and announcements
                directly to WhatsApp.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Automated fee reminders and payment confirmations
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Daily attendance notifications to parents
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Bulk announcements for events and holidays
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Two-way communication for better engagement
                  </span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-8 rounded-2xl shadow-2xl">
                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-emerald rounded-full flex items-center justify-center">
                      <Send className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy">VibhumEduSys</p>
                      <p className="text-xs text-slate-500">Automated Messages</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        Dear Parent, fee payment of ₹5000 is due. Please clear
                        by 15th Jan.
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        Your child marked present today at 9:00 AM.
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        PTM scheduled on 20th Jan at 10:00 AM. Please attend.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            See All Features In Action
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Book a personalized demo to explore how VibhumEduSys can transform your
            school operations.
          </p>
          <Link to="/register">
            <Button
              data-testid="features-demo-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8 cta-button"
            >
              Book Free Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
